/**
 * หนังสือของฉัน — ฝั่งพนักงาน (/me/letters)
 *
 * 🚨 หน้านี้เกิดเพราะบทเรียนที่ซ้ำมากที่สุดของบ้านนี้: "ฟีเจอร์ครบแต่ไม่มีทางเข้า"
 * รอบก่อนเปิด API `/me/letters` + เขียน hook ไว้แล้ว แต่ไม่มีหน้าจอ ไม่มีเมนู ไม่มีใครเรียก
 * ขณะที่กล่องยืนยันฝั่ง HR เขียนไว้ว่า "พนักงานจะเห็นเอกสารในเมนูของตัวเอง" = สัญญาลอย
 *
 * พนักงานเห็นได้เฉพาะฉบับที่ **เคยออกจริง** เท่านั้น (ร่างที่ HR ยังคิดอยู่ หรือร่างที่ยกเลิกทิ้ง
 * โดยไม่เคยออก ต้องไม่โผล่) — ด่านอยู่ทั้งที่ handler และ RLS
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, FileText, PenLine, CheckCircle2 } from 'lucide-react';
import { PageLayout } from '../components/layout/PageLayout';
import { LoadError } from '../components/layout/LoadError';
import { EmptyState } from '../components/ui/EmptyState';
import { useToast } from '../components/ui/toast';
import { useMyLetters, type MyLetterRow } from '../hooks';
import { apiDownload } from '../lib/api-client';
import { MyTawi50Card } from '../components/MyTawi50Card';

function thaiDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('th-TH', {
        timeZone: 'Asia/Bangkok', day: 'numeric', month: 'short', year: 'numeric',
      });
}

function Skeleton() {
  return (
    <div className="space-y-3 animate-pulse" aria-label="กำลังโหลดเอกสาร">
      {[0, 1].map((k) => (
        <div key={k} className="card p-4">
          <div className="h-4 w-3/4 bg-gray-100 rounded" />
          <div className="h-3 w-1/2 bg-gray-100 rounded mt-2" />
        </div>
      ))}
    </div>
  );
}

export function MyLetters() {
  const { data, isLoading, error, refetch } = useMyLetters();
  const toast = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);

  async function download(row: MyLetterRow) {
    setBusyId(row.id);
    try {
      await apiDownload(
        `/me/letters/${encodeURIComponent(row.id)}/pdf`,
        `${row.letter_type}-${(row.doc_no ?? '').replace('/', '-')}.pdf`,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ดาวน์โหลดไม่สำเร็จ');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <PageLayout title="หนังสือของฉัน">
      <div className="space-y-4 pb-8">
        {/* ใบ 50 ทวิ ของตัวเอง (fb#176) — อยู่บนสุดเพราะเป็นเอกสารที่พนักงานถามหาบ่อยที่สุดช่วงยื่นภาษี */}
        <MyTawi50Card />
        {isLoading ? (
          <Skeleton />
        ) : error || !data ? (
          <LoadError error={error} onRetry={() => void refetch()} />
        ) : data.items.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="ยังไม่มีหนังสือ"
            /* ห้ามสัญญาว่า "เก็บไว้ได้ตลอด" — ฝ่ายบุคคลยกเลิกฉบับที่ออกผิดได้ แล้วฉบับนั้นจะหายจากหน้านี้
               บอกตามจริงว่าให้ดาวน์โหลดเก็บไฟล์ไว้เอง */
            detail="เมื่อฝ่ายบุคคลออกหนังสือรับรองหรือหนังสือเตือนให้ เอกสารจะมาแสดงที่นี่ แนะนำให้ดาวน์โหลดเก็บไฟล์ไว้เอง เพราะถ้าฝ่ายบุคคลยกเลิกฉบับนั้น เอกสารจะหายไปจากหน้านี้"
          />
        ) : (
          <>
            {/* ตัดที่ 200 ฉบับเงียบ ๆ = เจ้าตัวคิดว่าเอกสารเก่าถูกลบทิ้ง */}
            {data.has_more && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
                แสดง {data.limit} ฉบับล่าสุดเท่านั้น ยังมีที่เก่ากว่านี้อีก — ถ้าต้องการฉบับเก่า กรุณาแจ้งฝ่ายบุคคลครับ
              </div>
            )}
            {data.items.map((row) => (
            <div key={row.id} className="card p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-bold">{row.title}</div>
                  <div className="text-sm text-gray-500 mt-0.5">
                    เลขที่ {row.doc_no ?? '—'} · ออกเมื่อ {thaiDate(row.issued_at)}
                  </div>
                  {row.status === 'cancelled' && (
                    <div className="mt-2 inline-block rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      ยกเลิกแล้ว — เอกสารฉบับนี้ใช้อ้างอิงไม่ได้
                    </div>
                  )}
                  {row.ack && (
                    <div className="mt-2 inline-flex items-center gap-1 rounded bg-green-50 px-2 py-0.5 text-xs text-green-700">
                      <CheckCircle2 className="w-3.5 h-3.5" /> ลงนามรับทราบแล้ว {thaiDate(row.ack.method === 'paper' ? row.ack.signed_on : row.ack.acked_at)}
                    </div>
                  )}
                  {row.needs_ack && (
                    <Link
                      to={`/me/letters/${encodeURIComponent(row.id)}/sign`}
                      className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-2 text-sm font-medium text-white hover:bg-amber-600 min-h-11"
                    >
                      <PenLine className="w-4 h-4" /> อ่านและลงนามรับทราบ
                    </Link>
                  )}
                </div>
                <button
                  onClick={() => download(row)}
                  disabled={busyId === row.id}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
                >
                  <Download className="w-4 h-4" />
                  {busyId === row.id ? 'กำลังโหลด…' : 'ดาวน์โหลด'}
                </button>
              </div>
            </div>
            ))}
          </>
        )}
      </div>
    </PageLayout>
  );
}
